import {
  PerformanceReportingMetricsResponse,
  PerformanceReportingTrendsResponse,
  PerformanceReportingDimensionsResponse,
  PerformanceReportingTop5SummariesResponse,
  TrendValue,
} from '../../_types';
import {
  INITIAL_REASON_VALUES,
  HOW_TERMINATED_VALUES,
  TERMINATION_TACTIC_VALUES,
} from './vehiclePursuitsLookups';
import { formatTooltipDescription } from '../../_constants';

/**
 * Type representing a Vehicle Pursuit event record
 * 
 * This matches the structure of records in vp_events.json with additional
 * fields for OrgId, InitialReason, HowTerminated, and TerminationTactic.
 * These records will be dynamically generated and used as base data to
 * calculate metrics.
 */
export interface VehiclePursuitEvent {
  OrgId: string; // UUID of the organization
  EventDate: string; // ISO date string (YYYY-MM-DD)
  OfficerCount: number; // Number of officers (1-5, leaning toward fewer)
  OfficerInjury: 'Y' | 'N'; // Whether any officer was injured
  FatalOfficerInjury: 'Y' | 'N'; // Whether any officer injury was fatal
  SuspectCount: number; // Number of suspects (0-5, more suspects = more officers)
  SuspectInjury: 'Y' | 'N'; // Whether any suspect was injured
  FatalSuspectInjury: 'Y' | 'N'; // Whether any suspect injury was fatal
  OfficerVehicleDamage: 'Y' | 'N'; // Whether officer vehicle was damaged
  SuspectVehicleDamage: 'Y' | 'N'; // Whether suspect vehicle was damaged
  InitialReason: string; // Initial reason for pursuit (from INITIAL_REASON_VALUES)
  HowTerminated: string; // How the pursuit was terminated (from HOW_TERMINATED_VALUES)
  TerminationTactic: string; // Termination tactic used (from TERMINATION_TACTIC_VALUES)
}

/**
 * Helper to select a random item from an array
 */
function randomItem<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * Helper to generate a random date between start and end dates
 */
function randomDate(startDate: string, endDate: string): string {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const randomTime = start + Math.random() * (end - start);
  return new Date(randomTime).toISOString().split('T')[0];
}

/**
 * Helper to generate OfficerCount (1-5, heavily skewed to 1-2)
 * Based on sample data: 1 (55%), 2 (26%), 3 (11%), 4 (5%), 5 (2%)
 */
function generateOfficerCount(): number {
  const rand = Math.random();
  if (rand < 0.55) return 1;
  if (rand < 0.81) return 2;
  if (rand < 0.92) return 3;
  if (rand < 0.97) return 4;
  return 5;
}

/**
 * Helper to generate SuspectCount (0-5, heavily skewed to 1)
 * Based on sample data: 1 (87%), 2 (5%), 3 (1.4%), 4 (0.6%), 0 (6%)
 * Also considers OfficerCount - more officers = slightly higher chance of more suspects
 */
function generateSuspectCount(officerCount: number): number {
  const rand = Math.random();

  // If there are more officers (3+), slightly shift probability toward more suspects
  // by reducing the probability of single suspect and 0 suspects
  if (officerCount >= 3) {
    if (rand < 0.80) return 1; // Reduced from 87%
    if (rand < 0.88) return 2; // Increased from 5%
    if (rand < 0.95) return 3; // Increased from 1.4%
    if (rand < 0.98) return 4; // Increased from 0.6%
    if (rand < 0.995) return 0; // Reduced from 6%
    return 5;
  }

  // Standard distribution for 1-2 officers
  if (rand < 0.87) return 1;
  if (rand < 0.92) return 2;
  if (rand < 0.936) return 3;
  if (rand < 0.942) return 4;
  if (rand < 0.998) return 0; // Some events have 0 suspects
  return 5;
}

/**
 * Generate a single Vehicle Pursuit event
 */
function generateEvent(
  orgId: string,
  startDate: string,
  endDate: string
): VehiclePursuitEvent {
  const officerCount = generateOfficerCount();
  const suspectCount = generateSuspectCount(officerCount);

  // Generate InitialReason (equal probability for now, can be weighted later)
  const initialReason = randomItem(INITIAL_REASON_VALUES);

  // Generate HowTerminated (equal probability for now, can be weighted later)
  const howTerminated = randomItem(HOW_TERMINATED_VALUES);

  // Generate TerminationTactic (equal probability for now, can be weighted later)
  const terminationTactic = randomItem(TERMINATION_TACTIC_VALUES);

  // OfficerInjury: 11% Y, 89% N
  const officerInjury: 'Y' | 'N' = Math.random() < 0.11 ? 'Y' : 'N';

  // FatalOfficerInjury: Higher chance for demo purposes (about 30-35% of injuries are fatal)
  // This gives roughly 3-4% of all events having fatal officer injuries
  const fatalOfficerInjury: 'Y' | 'N' =
    officerInjury === 'Y' && Math.random() < 0.35 ? 'Y' : 'N';

  // SuspectInjury: 23% Y, 77% N
  const suspectInjury: 'Y' | 'N' = Math.random() < 0.23 ? 'Y' : 'N';

  // FatalSuspectInjury: Higher chance for demo purposes (about 25-30% of injuries are fatal)
  // This gives roughly 6-7% of all events having fatal suspect injuries
  const fatalSuspectInjury: 'Y' | 'N' =
    suspectInjury === 'Y' && Math.random() < 0.30 ? 'Y' : 'N';

  // Vehicle damage: 20-30% of events have vehicle damage
  // OfficerVehicleDamage: 15% Y, 85% N
  const officerVehicleDamage: 'Y' | 'N' = Math.random() < 0.15 ? 'Y' : 'N';

  // SuspectVehicleDamage: 25% Y, 75% N (slightly higher than officer damage)
  const suspectVehicleDamage: 'Y' | 'N' = Math.random() < 0.25 ? 'Y' : 'N';

  return {
    OrgId: orgId,
    EventDate: randomDate(startDate, endDate),
    OfficerCount: officerCount,
    OfficerInjury: officerInjury,
    FatalOfficerInjury: fatalOfficerInjury,
    SuspectCount: suspectCount,
    SuspectInjury: suspectInjury,
    FatalSuspectInjury: fatalSuspectInjury,
    OfficerVehicleDamage: officerVehicleDamage,
    SuspectVehicleDamage: suspectVehicleDamage,
    InitialReason: initialReason,
    HowTerminated: howTerminated,
    TerminationTactic: terminationTactic,
  };
}

/**
 * Generate base Vehicle Pursuit event data
 * 
 * Generates a collection of events with weighted randomization based on
 * patterns found in the sample data. The number of events is determined
 * by the date range - approximately 1-3 events per day on average.
 */
export function generateVehiclePursuitBaseData(
  orgId: string,
  startDate: string,
  endDate: string
): VehiclePursuitEvent[] {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  // Generate approximately 1-3 events per day on average
  const avgEventsPerDay = 1.5 + Math.random(); // 1.5 to 2.5
  const numEvents = Math.floor(daysDiff * avgEventsPerDay);

  const events: VehiclePursuitEvent[] = [];
  for (let i = 0; i < numEvents; i++) {
    events.push(generateEvent(orgId, startDate, endDate));
  }

  return events;
}

/**
 * Generator for Vehicle Pursuits module data
 */
export class VehiclePursuitsGenerator {
  /**
   * Generate metrics for Vehicle Pursuits module
   */
  static generateMetrics(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingMetricsResponse {
    const baseData = generateVehiclePursuitBaseData(orgId, startDate, endDate);

    // Total incidents is the count of events in the base data
    const totalIncidents = baseData.length;

    // Fatal incidents are events where either FatalOfficerInjury or FatalSuspectInjury is 'Y'
    const fatalIncidents = baseData.filter(
      (event) => event.FatalOfficerInjury === 'Y' || event.FatalSuspectInjury === 'Y'
    ).length;

    // Crash incidents are events where either OfficerVehicleDamage or SuspectVehicleDamage is 'Y'
    const crashIncidents = baseData.filter(
      (event) => event.OfficerVehicleDamage === 'Y' || event.SuspectVehicleDamage === 'Y'
    ).length;

    const percentFatalIncidents =
      totalIncidents > 0
        ? Math.round((fatalIncidents / totalIncidents) * 1000) / 1000
        : 0;

    const percentEndingInCrash =
      totalIncidents > 0
        ? Math.round((crashIncidents / totalIncidents) * 1000) / 1000
        : 0;

    const metrics: PerformanceReportingMetricsResponse = [
      {
        key: 'totalIncidents',
        label: 'Total Vehicle Pursuit Incidents',
        value: totalIncidents,
        valueFormat: 'number',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description:
          'Total number of completed vehicle pursuit incidents during the selected date range',
      },
      {
        key: 'percentEndingInCrash',
        label: 'Pursuits Ending in Crash',
        value: percentEndingInCrash,
        valueFormat: 'percent',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description: 'Percentage of pursuits that ended in a crash',
      },
      {
        key: 'percentFatalIncidents',
        label: 'Incidents with Fatalities',
        value: percentFatalIncidents,
        valueFormat: 'percent',
        trend: Math.random() < 0.5 ? ('DOWN' as TrendValue) : ('UP' as TrendValue),
        description: 'Percentage of incidents with fatalities',
      },
    ];

    return metrics;
  }

  /**
   * Calculate number of periods based on date range
   * If 31 days or less, use weeks; otherwise use months
   */
  private static calculatePeriods(
    startDate: string,
    endDate: string
  ): { useWeeks: boolean; periods: number; } {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    // If period is 31 days or less, use weeks
    const useWeeks = diffDays <= 31;

    if (useWeeks) {
      // Calculate number of weeks
      const weeks = Math.ceil(diffDays / 7);
      return { useWeeks: true, periods: Math.max(1, weeks) };
    }

    // For more than a month, calculate number of complete months
    const monthsDiff =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1; // +1 to include both start and end months

    return { useWeeks: false, periods: Math.max(1, monthsDiff) };
  }

  /**
   * Get the period start date for a given period index
   */
  private static getPeriodStartDate(
    startDate: string,
    periodIndex: number,
    useWeeks: boolean
  ): Date {
    const start = new Date(startDate);
    if (useWeeks) {
      const periodDate = new Date(start);
      periodDate.setDate(start.getDate() + periodIndex * 7);
      return periodDate;
    } else {
      return new Date(start.getFullYear(), start.getMonth() + periodIndex, 1);
    }
  }

  /**
   * Determine which period an event belongs to
   */
  private static getEventPeriod(
    eventDate: string,
    startDate: string,
    useWeeks: boolean
  ): number {
    const event = new Date(eventDate);
    const start = new Date(startDate);

    if (useWeeks) {
      const diffDays = Math.floor(
        (event.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
      );
      return Math.floor(diffDays / 7);
    } else {
      const monthsDiff =
        (event.getFullYear() - start.getFullYear()) * 12 +
        (event.getMonth() - start.getMonth());
      return monthsDiff;
    }
  }

  /**
   * Generate trends data for Vehicle Pursuits module
   */
  static generateTrends(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingTrendsResponse {
    const baseData = generateVehiclePursuitBaseData(orgId, startDate, endDate);
    const { useWeeks, periods } = VehiclePursuitsGenerator.calculatePeriods(
      startDate,
      endDate
    );
    const periodMode = useWeeks ? 'week' : 'month';

    // Group events by period
    const periodData: Array<{
      total: number;
      crash: number;
    }> = Array(periods).fill(null).map(() => ({ total: 0, crash: 0 }));

    baseData.forEach((event) => {
      const periodIndex = VehiclePursuitsGenerator.getEventPeriod(
        event.EventDate,
        startDate,
        useWeeks
      );

      // Only count events within the valid period range
      if (periodIndex >= 0 && periodIndex < periods) {
        periodData[periodIndex].total++;
        if (
          event.OfficerVehicleDamage === 'Y' ||
          event.SuspectVehicleDamage === 'Y'
        ) {
          periodData[periodIndex].crash++;
        }
      }
    });

    // Build trend series data
    const totalData = periodData.map((data, index) => {
      const periodDate = VehiclePursuitsGenerator.getPeriodStartDate(
        startDate,
        index,
        useWeeks
      );
      const period = periodDate.toISOString().split('T')[0];

      return {
        period,
        value: data.total,
        description: [
          `${data.total} incident${data.total === 1 ? '' : 's'}`,
          'Total pursuits',
        ],
      };
    });

    const crashData = periodData.map((data, index) => {
      const periodDate = VehiclePursuitsGenerator.getPeriodStartDate(
        startDate,
        index,
        useWeeks
      );
      const period = periodDate.toISOString().split('T')[0];

      return {
        period,
        value: data.crash,
        description: [
          `${data.crash} incident${data.crash === 1 ? '' : 's'}`,
          'Pursuits ending in crash',
        ],
      };
    });

    return {
      periodMode,
      series: [
        {
          name: 'total',
          data: totalData,
        },
        {
          name: 'crashes',
          data: crashData,
        },
      ],
    };
  }

  /**
   * Generate dimensions data for Vehicle Pursuits module
   */
  static generateDimensions(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingDimensionsResponse {
    const baseData = generateVehiclePursuitBaseData(orgId, startDate, endDate);
    const dimensions: PerformanceReportingDimensionsResponse = [];
    const subject = 'incident';

    // Dimension 1: Initial Reason
    const initialReasonCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      initialReasonCounts[event.InitialReason] =
        (initialReasonCounts[event.InitialReason] || 0) + 1;
    });

    const totalInitialReasons = baseData.length;
    const initialReasonData = Object.entries(initialReasonCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalInitialReasons, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'Initial Reason',
      data: initialReasonData,
    });

    // Dimension 2: How Terminated
    const howTerminatedCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      howTerminatedCounts[event.HowTerminated] =
        (howTerminatedCounts[event.HowTerminated] || 0) + 1;
    });

    const totalHowTerminated = baseData.length;
    const howTerminatedData = Object.entries(howTerminatedCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalHowTerminated, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'How Terminated',
      data: howTerminatedData,
    });

    // Dimension 3: Termination Tactic
    const terminationTacticCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      terminationTacticCounts[event.TerminationTactic] =
        (terminationTacticCounts[event.TerminationTactic] || 0) + 1;
    });

    const totalTerminationTactics = baseData.length;
    const terminationTacticData = Object.entries(terminationTacticCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalTerminationTactics, subject),
      }))
      .sort((a, b) => b.value - a.value); // Sort descending

    dimensions.push({
      name: 'Termination Tactic',
      data: terminationTacticData,
    });

    return dimensions;
  }

  /**
   * Generate top-5 summaries for Vehicle Pursuits module
   */
  static generateTop5Summaries(
    orgId: string,
    startDate: string,
    endDate: string
  ): PerformanceReportingTop5SummariesResponse {
    const baseData = generateVehiclePursuitBaseData(orgId, startDate, endDate);
    const subject = 'incident';
    const summaries: PerformanceReportingTop5SummariesResponse = [];

    // Summary 1: Outcome of Pursuit (simplified categories)
    let noCrash = 0; // No vehicle damage and no injuries
    let crashOnly = 0; // Any vehicle damage but no injuries
    let injuryNoFatality = 0; // Any non-fatal injuries, regardless of crashes
    let fatalities = 0; // Any fatal injuries, regardless of crashes

    baseData.forEach((event) => {
      const hasVehicleDamage =
        event.OfficerVehicleDamage === 'Y' || event.SuspectVehicleDamage === 'Y';
      const hasSuspectInjury = event.SuspectInjury === 'Y' && event.FatalSuspectInjury === 'N';
      const hasOfficerInjury = event.OfficerInjury === 'Y' && event.FatalOfficerInjury === 'N';
      const hasSuspectFatality = event.FatalSuspectInjury === 'Y';
      const hasOfficerFatality = event.FatalOfficerInjury === 'Y';
      const hasAnyInjury = hasSuspectInjury || hasOfficerInjury;
      const hasAnyFatality = hasSuspectFatality || hasOfficerFatality;

      // Count incidents in each category (incidents can be in multiple categories)
      if (!hasVehicleDamage && !hasAnyInjury && !hasAnyFatality) {
        noCrash++;
      }
      if (hasVehicleDamage && !hasAnyInjury && !hasAnyFatality) {
        crashOnly++;
      }
      if (hasAnyInjury && !hasAnyFatality) {
        injuryNoFatality++;
      }
      if (hasAnyFatality) {
        fatalities++;
      }
    });

    const totalIncidents = baseData.length;

    const outcomeData = [
      {
        name: 'No Crash',
        value: noCrash,
        description: formatTooltipDescription(noCrash, totalIncidents, subject),
      },
      {
        name: 'Crash Only',
        value: crashOnly,
        description: formatTooltipDescription(crashOnly, totalIncidents, subject),
      },
      {
        name: 'Injury, No Fatality',
        value: injuryNoFatality,
        description: formatTooltipDescription(injuryNoFatality, totalIncidents, subject),
      },
      {
        name: 'Fatalities',
        value: fatalities,
        description: formatTooltipDescription(fatalities, totalIncidents, subject),
      },
    ].sort((a, b) => b.value - a.value); // Sort descending

    summaries.push({
      name: 'Outcome of Pursuit',
      data: outcomeData,
    });

    // Summary 2: Initial Reason (top 5)
    const initialReasonCounts: Record<string, number> = {};
    baseData.forEach((event) => {
      initialReasonCounts[event.InitialReason] =
        (initialReasonCounts[event.InitialReason] || 0) + 1;
    });

    // Get total count for percentage calculation
    const totalInitialReasons = Object.values(initialReasonCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    // Sort by count descending and take top 5
    const top5InitialReasons = Object.entries(initialReasonCounts)
      .map(([name, value]) => ({
        name,
        value,
        description: formatTooltipDescription(value, totalInitialReasons, subject),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    summaries.push({
      name: 'Initial Reason',
      data: top5InitialReasons,
    });

    return summaries;
  }
}
